// frontend/src/components/CustomerFormModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-09-23
// Updated:      2026-09-23
//
// Description:
//   Mobile-first modal host for the canonical CustomerFormWidget. The wrapper
//   owns only modal presentation, close behaviour and post-create notification;
//   the widget remains authoritative for fields, validation and persistence.

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useTheme } from "../context/ThemeContext.jsx";
import CustomerFormWidget from "../widgets/CustomerFormWidget";

export default function CustomerFormModal({
  open = false,
  onClose,
  onCreateContact,
  onCustomerCreated,
}) {
  const { theme } = useTheme();

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const handleCustomerSaved = (customer) => {
    onCustomerCreated(customer);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[150500] flex items-center justify-center bg-black/45 p-0 sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="vision-customer-form-modal-title"
        className="flex h-[100dvh] max-h-[100dvh] w-full max-w-5xl flex-col overflow-hidden rounded-none shadow-2xl sm:h-auto sm:max-h-[92vh] sm:rounded-2xl"
        style={{
          background: "#ffffff",
          color: "#1f2937",
          fontFamily: theme?.font_body || "Arial, sans-serif",
        }}
      >
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-black/10 px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <h2
              id="vision-customer-form-modal-title"
              className="truncate text-lg font-bold"
              style={{ fontFamily: theme?.font_header || "inherit" }}
            >
              New Customer
            </h2>
            <p className="text-xs opacity-65">
              Create a customer using the standard Vision customer form.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-btnGeneralNormal text-xl font-bold leading-none text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
            aria-label="Close customer form"
            title="Close"
          >
            ×
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 sm:px-6 sm:py-6">
          <CustomerFormWidget
            customer={null}
            onClose={onClose}
            onCustomerCreated={onCreateContact}
            onCustomerSaved={handleCustomerSaved}
          />
        </div>
      </section>
    </div>,
    document.body
  );
}

CustomerFormModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreateContact: PropTypes.func.isRequired,
  onCustomerCreated: PropTypes.func.isRequired,
};
